import Link from "next/link";
import { useRouter } from "next/router";
import useSWR from "swr";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { filter } from "lodash";
import { twMerge } from "tailwind-merge";

import { faSlidersH } from "@fortawesome/free-solid-svg-icons";
import { faAngleDoubleLeft } from "@fortawesome/free-solid-svg-icons/faAngleDoubleLeft";
import { faAngleDoubleRight } from "@fortawesome/free-solid-svg-icons/faAngleDoubleRight";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import useActivityTracker from "hooks/useActivityTracker";
import useAuthentication from "hooks/useAuthentication";
import { useGlobalState } from "hooks/useGlobalState";
import useMediaQuery from "hooks/useMediaQuery";
import useWindowSize from "hooks/useWindowSize";
import { baseFetcher } from "utils/baseFetcher";

import DiscussionPageFocusModal from "../DiscussionPageFocusedModal";

import styles from "./style.module.scss";

export type Tab = {
  filters: string[];
  name: string;
  href: string;
  pills?: { filter: string; name: string }[];
  mobile?: boolean;
};

export const tabs: ("separator" | Tab)[] = [
  {
    filters: ["for-me"],
    name: "For Me",
    href: "/discussions?tab=for-me",
    mobile: true,
  },
  {
    filters: [
      "from-watchlist-or-followed-ips",
      "from-followed-ips",
      "from-watchlist",
    ],
    pills: [
      { filter: "from-watchlist-or-followed-ips", name: "All" },
      { filter: "from-followed-ips", name: "Insight Providers" },
      { filter: "from-watchlist", name: "Watchlists" },
    ],
    name: "Following",
    href: "/discussions?tab=from-watchlist-or-followed-ips",
  },
  {
    filters: ["all"],
    name: "All",
    href: "/discussions?tab=all",
    mobile: true,
  },
  "separator",
  {
    filters: ["on-or-by-account"],
    name: "Posts & Mentions",
    href: "/discussions?tab=on-or-by-account",
  },
];

type DashboardPageTabsProps = {};

const DiscussionPageTabs = ({}: DashboardPageTabsProps) => {
  const router = useRouter();
  const tabQuery = router.query.tab as string;

  const { account } = useAuthentication();
  const { mobile } = useMediaQuery();

  const { data: phoneNumber, isValidating: isPhoneNumberValidating } = useSWR(
    account?.id ? `/api/v2/accounts/${account?.id}/phone-number` : null,
    baseFetcher
  );

  const isUnverifiedPreviewPass =
    account?.isPreviewPass &&
    !isPhoneNumberValidating &&
    !(phoneNumber?.verified || phoneNumber?.skipVerification);
  const { trackActivity } = useActivityTracker();

  const [_, setShowDiscussionComposeModal] = useGlobalState(
    "showDiscussionComposeModal",
    false
  );

  const scrollableRef = useRef<HTMLDivElement>(null);
  const { width } = useWindowSize();
  const [showLeftNav, setShowLeftNav] = useState(false);
  const [showRightNav, setShowRightNav] = useState(false);
  const scroll = useCallback(
    (direction) => {
      const scrollable = scrollableRef.current;
      const scrollLeft = scrollable?.scrollLeft || 0;
      const offset = (direction === "right" ? 1 : -1) * Math.round(width / 2);
      scrollable?.scroll({ left: scrollLeft + offset, behavior: "smooth" });
    },
    [width]
  );
  const [showFocusModal, setShowFocusModal] = useState(false);

  const scrollToTab = useCallback((tab) => {
    const scrollable = scrollableRef.current;

    const scrollLeft =
      tab.offsetLeft -
      (scrollable?.offsetLeft || 0) +
      tab.offsetWidth -
      (scrollable?.scrollWidth || 0) / 2;

    scrollable?.scroll({
      left: scrollLeft,
      behavior: "smooth",
    });
  }, []);

  const onTabClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, tab) => {
      try {
        const tabTarget = event.currentTarget;
        scrollToTab(tabTarget);
      } catch {}
      try {
        if (
          tab.filters.includes(tabQuery) ||
          (!tabQuery && tab.filters.includes("all"))
        ) {
        } else {
          trackActivity("comment.view_thread", undefined, {
            type: "list",
            tab: tab.filters?.[0],
          });
        }
      } catch {}
    },
    [scrollToTab, tabQuery, trackActivity]
  );

  const activeTab = useRef<HTMLSpanElement | null>(null);
  const onTabLoad = useCallback(
    (ref, tab) => {
      if (
        !!ref &&
        (router.asPath === tab.href || router.asPath.startsWith(`${tab.href}?`))
      ) {
        activeTab.current = ref;
      }
    },
    [router.asPath]
  );
  useEffect(() => {
    if (activeTab.current) scrollToTab(activeTab.current);
  }, [scrollToTab]);

  useEffect(() => {
    const scrollable = scrollableRef.current;

    const updateNavButtons = () => {
      if (scrollable?.scrollLeft === 0) {
        setShowLeftNav(false);
      } else {
        setShowLeftNav(true);
      }
      if (
        (scrollable?.scrollLeft || 0) + width >=
        (scrollable?.scrollWidth || 0)
      ) {
        setShowRightNav(false);
      } else {
        setShowRightNav(true);
      }
    };

    updateNavButtons();

    scrollable?.addEventListener("scroll", updateNavButtons, { passive: true });

    return () => {
      scrollable?.removeEventListener("scroll", updateNavButtons);
    };
  }, [width]);

  const [top, setTop] = useState(54);

  useEffect(() => {
    if (!mobile) return;

    const upperNavBar = 54;

    const updateTabsPosition = () => {
      const top = document.body.getBoundingClientRect()?.top || 0;
      setTimeout(() => {
        setTop(
          top > -upperNavBar - 54
            ? 54
            : top + upperNavBar < 0
            ? 0
            : top + upperNavBar
        );
      }, 100);
    };

    updateTabsPosition();

    window.addEventListener("scroll", updateTabsPosition, { passive: true });

    return () => {
      window.removeEventListener("scroll", updateTabsPosition);
    };
  }, [mobile]);

  const filteredTabs = useMemo(() => {
    return filter(tabs, (tab: any) => {
      if (mobile) {
        return tab.mobile;
      }

      return tab;
    });
  }, [mobile]);

  const floatingButton = useMemo(() => {
    if (isUnverifiedPreviewPass) return null;

    return (
      <OverlayTrigger
        overlay={
          account?.cannotPostCommentReason ||
          account?.cannotViewCommentReason ? (
            <Tooltip>
              Your Compliance Team has restricted your access to this feature
            </Tooltip>
          ) : (
            <></>
          )
        }
      >
        <button
          disabled={!account?.canPostComment}
          className={twMerge(
            "border-0 bg-smart-teal hover:bg-dark-teal",
            "rounded-full",
            "text-white font-bold leading-[1] text-[13px] md:text-[15px]",
            "py-[1.25em] px-[1.25em] md:py-[1em] md:mr-[25px] md:ml-[10px] min-w-[50px] h-[50px] md:h-fit md:w-full",
            "fixed right-[15px] bottom-[45px] z-10",
            "md:relative md:right-auto md:bottom-auto"
          )}
          onClick={() => {
            setShowDiscussionComposeModal(true);
          }}
        >
          <FontAwesomeIcon className="md:hidden" icon={faPlus} />
          <span className="hidden md:inline">Start New</span>
        </button>
      </OverlayTrigger>
    );
  }, [
    account?.canPostComment,
    account?.cannotPostCommentReason,
    account?.cannotViewCommentReason,
    isUnverifiedPreviewPass,
    setShowDiscussionComposeModal,
  ]);

  return (
    <>
      <div
        className={twMerge(
          styles.discussionsPageTabs,
          mobile && styles.mobileLayout,
          styles.stickyTabs,
          "!pt-0 z-50"
        )}
        style={{ top }}
      >
        <div className="flex justify-end md:justify-center mb-0 md:mb-[20px]">
          {(account?.canPostComment ||
            account?.cannotPostCommentReason ===
              "company-disable-posting-comments" ||
            account?.cannotViewCommentReason ===
              "company-disable-viewing-comments") &&
            floatingButton}
        </div>

        {showLeftNav && (
          <button
            title="scroll left"
            className={twMerge(styles.tabsNav, styles.left)}
            onClick={() => scroll("left")}
          >
            <FontAwesomeIcon icon={faAngleDoubleLeft} />
          </button>
        )}
        <div
          className={twMerge(
            styles.tabs,
            "border-b-[1px] md:border-b-0 border-x-0 border-t-0 border-solid border-inactive-gray md:shadow-none"
          )}
          ref={scrollableRef}
        >
          {filteredTabs.map((tab: any) => {
            if (tab === "separator") {
              return (
                <hr
                  key={tab}
                  className="hidden md:block ml-[10px] mr-[25px] mt-0"
                />
              );
            }

            return (
              <React.Fragment key={tab.name}>
                <Link
                  href={tab.href}
                  prefetch={false}
                  onClick={(e) => onTabClick(e, tab)}
                  className={twMerge("flex-grow md:flex-grow-0")}
                  ref={(ref) => onTabLoad(ref, tab)}
                >
                  <div className="flex items-center md:mb-[17px] justify-between">
                    <div
                      className={twMerge(
                        styles.item,
                        tab.filters.includes(tabQuery) ||
                          (!tabQuery && tab.filters.includes("all"))
                          ? styles.activeTab
                          : undefined,
                        "flex-1 md:flex-none"
                      )}
                    >
                      {tab.name}
                    </div>

                    {tab.name === "For Me" && !mobile && (
                      <OverlayTrigger
                        trigger={["hover", "focus"]}
                        overlay={<Tooltip>Modify Discussions Settings</Tooltip>}
                      >
                        <div className="absolute left-[120px]">
                          <FontAwesomeIcon
                            icon={faSlidersH}
                            className="text-[17px] text-smart-teal hover:text-dark-teal cursor-pointer"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setShowFocusModal(true);
                            }}
                          />
                        </div>
                      </OverlayTrigger>
                    )}
                  </div>
                </Link>
              </React.Fragment>
            );
          })}
        </div>
        {showRightNav && (
          <button
            title="scroll right"
            className={twMerge(styles.tabsNav, styles.right)}
            onClick={() => scroll("right")}
          >
            <FontAwesomeIcon icon={faAngleDoubleRight} />
          </button>
        )}
      </div>
      {showFocusModal && (
        <DiscussionPageFocusModal onClose={() => setShowFocusModal(false)} />
      )}
    </>
  );
};

export default DiscussionPageTabs;
